import {useProjectId} from "../../shopdrawing/ProjectName";
import React, {useContext} from "react";
import {ManagerLockContext} from "../../shopdrawing/ProjectActions";
import {hasPermission, useUser} from "../../../../misc/Permission";
import {useAlternative} from "../alternative/Alternative";
import {LongStringColWithExt, StringCol, Table} from "../../../../misc/scroller/Table";
import {api} from "../../../../api/API";
import {removeRowOption, RowOptionCol} from "../../../logistics/purchasing/RowOptions";
import {MiscPricingAutoInsert} from "../pricing/MiscPricingAutoInsert";
import {projectTablePrefName} from "../../shopdrawing/TablePrefName";
import {getAll} from "../../../logistics/routes/useGetAll";
import {QuoteMiscPrice} from "../../../../api/QuoteMiscPricing";
import {MiscPrice} from "../../../../api/MiscPricing";
import {Button} from "@mui/material";
import {useAsyncAction} from "nate-react-api-helpers";
import {HardwareGroupWithContent} from "../../../../api/Hardware";
import {useSnackbar} from "../../../../misc/Snackbar";
import {SaveAsPresetDialog} from "./SaveAsPresetDialog";

export function MiscTable() {
    const project = useProjectId();
    const {locked} = useContext(ManagerLockContext)
    const u = useUser();
    const alternative = useAlternative();
    const tableName = projectTablePrefName("misc", project);

    if(!u) return null;

    return (
        <Table<QuoteMiscPrice>
            key="misc-price"
            name={tableName}
            locked={(locked && !hasPermission(u, "EditShopDrawingWhenLockedToManager"))}
            fetch={(ctx) => getAll(ctx, offset => api.quoteMiscPricing.list({project: project, alternative, offset}))}
            onDrag={async (input) => {
                const details = {
                    project: project,
                    alternative: alternative,
                    idOrder: input.idOrder,
                }

                await api.quoteMiscPricing.reOrder(details as any)

                return {
                    sortByCol: null
                }
            }}
            columns={[
                !locked && RowOptionCol({
                    draggable: true,
                    options: (dt) => [
                        {
                            name: "Save as Preset",
                            onClick: (ctrl) => {
                                ctrl.onClose();
                                ctrl.display((onDone) => (
                                    <SaveAsPresetDialog
                                        category={dt.name}
                                        description={dt.publicNote}
                                        onDone={onDone}
                                    />
                                ));
                            },
                        },
                        removeRowOption(dt, {
                            refreshTables: [tableName],
                            onComplete: () => {}
                        }),
                    ],
                }),
                {
                    name: "Category",
                    editKey: "name",
                    editable: {
                        type: "lookup",
                        displayKey: "name",
                        freeSolo: true,
                        options: async (search) => {
                            const list = await api.quoteMiscPricing.listPresets({search: search, offset: 0});
                            return (list.data || []).map(p => ({display: p.category, value: p.category, original: p}));
                        },
                        onSelected: (row, opt) => {
                            row.name = opt.value;

                            // @ts-ignore
                            if(opt.original) {
                                // @ts-ignore
                                row.publicNote = opt.original.description;
                            }
                        }
                    },
                    render: (row) => row.name,
                },
                LongStringColWithExt("Description", "publicNote", (row: QuoteMiscPrice) => <DescriptionExtraButtons input={row} />, 400),
                StringCol("Notes", "internalNote"),
            ]}
            fetchDeps={[project, alternative]}
            onChange={(input) => api.quoteMiscPricing.upsert(input as any)}
            insert={{
                buttonText: "New Row",
                alignX: "left",
                modal: input => <MiscPricingAutoInsert onDone={input} />,
            }}
        />
    )
}

export function DescriptionExtraButtons(props: {
    input: QuoteMiscPrice | MiscPrice
}) {
    const snack = useSnackbar();

    const input = props.input;
    const action = useAsyncAction(async (input) => {
        const names: string[] = [];
        let list: HardwareGroupWithContent[] = [];

        if(input.quoteAlternative) {
            list = await api.quoteHardware.listWithContents({
                project: input.project,
                alternative: input.quoteAlternative,
            });
        } else {
            list = await api.hardware.listWithContents({
                project: input.project,
            });
        }

        const search = ["Automatic Operator", "Auto Operator"];
        list.forEach(l => {
            const has = l.hardware.some(v => {
                return search.some(searchFor => {
                    return v.category.indexOf(searchFor) !== -1
                })
            })

            if(!has) return;

            l.openings.forEach(o => {
                names.push(o.name);
            });
        })

        if(names.length === 0) {
            snack.error("No Auto Operator Openings Found (searching by contains 'Automatic Operator' or 'Auto Operator')");
            return;
        }

        const nameStr = names.join(", ");

        // copy to clipboard
        navigator.clipboard.writeText(nameStr).then(() => {
            snack.success("Copied to clipboard: " + nameStr);
        }, () => {
            snack.error("Failed to copy to clipboard");
        })

    }, []);

    return (
        <>
            {!!(action.loading || action.error) ? action.LoadingElement : null}
            <Button size="small" onClick={() => {
                action.callback({
                    project: input.project,
                    // @ts-ignore
                    quoteAlternative: input?.quoteAlternative,
                })
            }}>
                Auto-Operator Opening Numbers
            </Button>
        </>
    )
}
